import { useId } from 'react';
import type { ReactNode } from 'react';
import { useSearchParams } from 'react-router';
import { useApi } from '../api/context';
import type { BillingMonth, Invoice } from '../api/types';
import { Chip, ErrorNotice, Loading, PageHeader } from '../components/ui';
import { useI18n } from '../i18n';
import { formatDateTime, formatMonth, isValidMonth, skopjeMonth } from '../lib/dates';
import { formatDen } from '../lib/money';
import { useLoad } from '../lib/useLoad';

const INVOICE_MAX_ITEMS = 500;

/** The months to choose from: every month with fees (newest first) plus the current one. */
export function monthOptions(months: readonly BillingMonth[], current: string): string[] {
  const set = new Set<string>([current, ...months.map((m) => m.month)]);
  return [...set].filter(isValidMonth).sort((a, b) => (a < b ? 1 : a > b ? -1 : 0));
}

/** The monthly ZINQ invoice: fees and subscriptions by kind, then every item. */
export function InvoicePage() {
  const { t, locale } = useI18n();
  const api = useApi();
  const selectId = useId();
  const current = skopjeMonth();
  const [params, setParams] = useSearchParams();
  const requested = params.get('month');
  const month = requested && isValidMonth(requested) ? requested : current;

  const months = useLoad((_signal) => api.billingMonths(), [api]);
  const invoice = useLoad((signal) => api.invoice(month, signal), [api, month]);

  const options = monthOptions(months.state.status === 'ok' ? months.state.data : [], current);
  if (!options.includes(month)) options.unshift(month);

  let body: ReactNode;
  if (invoice.state.status === 'loading') body = <Loading />;
  else if (invoice.state.status === 'error') {
    body = <ErrorNotice error={invoice.state.error} onRetry={() => invoice.reload()} />;
  } else body = <InvoiceView invoice={invoice.state.data} />;

  return (
    <>
      <PageHeader
        title={t('invoice.title')}
        actions={
          <div className="inline-field">
            <label htmlFor={selectId}>{t('invoice.month')}</label>
            <select
              id={selectId}
              value={month}
              onChange={(e) => setParams({ month: e.target.value }, { replace: true })}
            >
              {options.map((m) => (
                <option key={m} value={m}>
                  {formatMonth(m, locale)}
                </option>
              ))}
            </select>
          </div>
        }
      />
      {body}
    </>
  );
}

/** One month's invoice (separate for tests). */
export function InvoiceView({ invoice }: { invoice: Invoice }) {
  const { t, locale } = useI18n();
  return (
    <>
      <section className="card invoice" aria-labelledby="invoice-heading">
        <div className="card-header">
          <h2 id="invoice-heading">{t('invoice.heading', { month: formatMonth(invoice.month, locale) })}</h2>
          <Chip tone={invoice.status === 'OPEN' ? 'info' : 'neutral'}>{t(`invoice.status.${invoice.status}`)}</Chip>
        </div>
        {invoice.status === 'OPEN' ? <p className="muted small">{t('invoice.openNote')}</p> : null}
        <div className="table-wrap">
          <table className="table" data-testid="invoice-lines">
            <thead>
              <tr>
                <th scope="col">{t('invoice.col.item')}</th>
                <th scope="col" className="num">
                  {t('invoice.col.count')}
                </th>
                <th scope="col" className="num">
                  {t('invoice.col.amount')}
                </th>
              </tr>
            </thead>
            <tbody>
              {invoice.lines.map((line) => (
                <tr key={line.kind} data-kind={line.kind}>
                  <th scope="row">{t(`invoice.kind.${line.kind}`)}</th>
                  <td className="num">{line.count}</td>
                  <td className="num">{formatDen(line.amountMinor)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <th scope="row">{t('invoice.total')}</th>
                <td />
                <td className="num strong" data-testid="invoice-total">
                  {formatDen(invoice.totalMinor)}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
        <p className="muted small">{t('invoice.note')}</p>
      </section>

      <section className="card" aria-labelledby="invoice-items">
        <div className="card-header">
          <h2 id="invoice-items">{t('invoice.items')}</h2>
        </div>
        {invoice.items.length === 0 ? (
          <p className="empty">{t('invoice.empty')}</p>
        ) : (
          <div className="table-wrap">
            <table className="table" data-testid="invoice-items">
              <thead>
                <tr>
                  <th scope="col">{t('invoice.col.date')}</th>
                  <th scope="col">{t('invoice.col.item')}</th>
                  <th scope="col">{t('invoice.col.reference')}</th>
                  <th scope="col" className="num">
                    {t('invoice.col.amount')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {invoice.items.map((item) => (
                  <tr key={item.id}>
                    <td className="nowrap">{formatDateTime(item.createdAt, locale)}</td>
                    <td>{t(`invoice.kind.${item.kind}`)}</td>
                    <td className="mono">{item.reference ?? '—'}</td>
                    <td className="num">{formatDen(item.amountMinor)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {invoice.items.length >= INVOICE_MAX_ITEMS ? <p className="muted small">{t('invoice.itemsLimit')}</p> : null}
      </section>
    </>
  );
}
